import React, { useState, useEffect, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { useDefaultDataStore } from '../../../stateManagers/store/useDefaultDataStore';

interface School {
  id: string;
  _id?: string;
  name: string;
  school_id_code?: string;
  state?: string;
  district?: string;
  pincode?: string;
  school_type?: string;
  status?: string;
  created_at?: string;
}

interface SchoolSettingsModalProps {
  visible: boolean;
  onHide: () => void;
  toast: React.RefObject<Toast | null>;
}

const SchoolSettingsModal: React.FC<SchoolSettingsModalProps> = ({ visible, onHide, toast }) => {
  const [allSchools, setAllSchools] = useState<School[]>([]);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [selectedSchoolId, setSelectedSchoolId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isActivating, setIsActivating] = useState(false);
  const [isSettingUpSchool, setIsSettingUpSchool] = useState(false);

  const { fetchDefaultData } = useDefaultDataStore();

  // Fetch schools when modal opens
  useEffect(() => {
    if (visible) {
      fetchSchools();
    } else {
      resetAllFields();
    }
  }, [visible]);

  const resetAllFields = useCallback(() => {
    setSelectedSchoolId(null);
    setError(null);
    setIsActivating(false);
  }, []);

  const fetchSchools = useCallback(async () => {
    setLoadingSchools(true);
    setError(null);
    try {
      console.log('Fetching schools from /api/schools...');
      
      const response = await apiClient.get('/api/schools');
      console.log('Schools response:', response);
      
      let schoolsData: School[] = [];
      
      // Handle different response formats
      if (Array.isArray(response.data)) {
        schoolsData = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        schoolsData = response.data.data;
      } else if (response.data?.schools && Array.isArray(response.data.schools)) {
        schoolsData = response.data.schools;
      }

      // Format schools with proper IDs
      const formattedSchools = schoolsData.map((school: any) => ({
        ...school,
        id: school._id || school.id || school.school_id,
        _id: school._id || school.id || school.school_id,
        name: school.name || school.school_name || 'Unnamed School',
      }));

      setAllSchools(formattedSchools);
      
      if (formattedSchools.length === 0) {
        showToast('warn', 'Warning', 'No schools found in the system.');
      }
    } catch (err: any) {
      console.error('Error fetching schools:', err);
      
      let errorMessage = 'Failed to load schools. Please try again.';
      
      if (err.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (err.response?.status === 403) {
        errorMessage = 'You do not have permission to view schools.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      showToast('error', 'Error', errorMessage);
      setAllSchools([]);
    } finally {
      setLoadingSchools(false);
    }
  }, []);

  // Activate School - Post to /superuser-meta/bulk
  const handleActivateSchool = async () => {
    if (!selectedSchoolId) {
      showToast('warn', 'Warning', 'Please select a school');
      return;
    }

    const selectedSchool = allSchools.find(s => s.id === selectedSchoolId);
    if (!selectedSchool) {
      showToast('error', 'Error', 'Selected school not found');
      return;
    }

    setIsActivating(true);
    setError(null);

    try {
      // Prepare the bulk meta data
      const metaData = {
        meta: [
          { 
            meta_option: "active_school_id", 
            meta_value: selectedSchool._id || selectedSchool.id 
          },
          { 
            meta_option: "active_school_id_code", 
            meta_value: selectedSchool.school_id_code || "" 
          },
          { 
            meta_option: "active_school_name", 
            meta_value: selectedSchool.name 
          }
        ]
      };

      console.log('Activating school with meta:', metaData);

      // Post to bulk meta endpoint
      const response = await apiClient.post('/superuser-meta/bulk', metaData);

      if (response.data.status === 'success') {
        showToast('success', 'Success', `${selectedSchool.name} activated successfully!`);
        
        // Close modal
        onHide();
        
        // Show full-screen loading overlay
        setIsSettingUpSchool(true);
        
        // Fetch default data to refresh the app state
        await fetchDefaultData();
        
        // Small delay to ensure data is loaded
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Hide loading overlay
        setIsSettingUpSchool(false);
        
        // Reset selection
        setSelectedSchoolId(null);
      } else {
        showToast('error', 'Error', response.data.message || 'Failed to activate school.');
      }
    } catch (err: any) {
      console.error('Error activating school:', err);
      
      let errorMessage = 'Failed to activate school. Please try again.';
      
      if (err.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (err.response?.status === 403) {
        errorMessage = 'You do not have permission to perform this action.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }
      
      setError(errorMessage);
      showToast('error', 'Error', errorMessage);
      setIsSettingUpSchool(false);
    } finally {
      setIsActivating(false);
    }
  };

  const showToast = (severity: 'success' | 'warn' | 'error', summary: string, detail: string) => {
    toast?.current?.show({ severity, summary, detail, life: severity === 'error' ? 5000 : 3000 });
  };

  const getStatusColor = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return '#059669';
      case 'inactive':
        return '#dc2626';
      case 'suspended':
        return '#d97706';
      case 'pending':
        return '#2563eb';
      default:
        return '#6b7280';
    }
  };

  const renderRefreshButton = () => (
    <Button
      icon="pi pi-refresh"
      tooltip="Refresh schools list"
      tooltipOptions={{ position: 'left' }}
      onClick={fetchSchools}
      disabled={loadingSchools}
      text
      rounded
      style={{
        width: '36px',
        height: '36px',
        color: '#2855ae',
      }}
    />
  );

  const renderSchoolList = () => (
    <div style={{ padding: '20px 0' }}>
      {/* Error Display */}
      {error && (
        <div style={{
          background: '#fef2f2',
          border: '1px solid #fca5a5',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
        }}>
          <i className="pi pi-exclamation-circle" style={{ fontSize: '20px', color: '#dc2626', marginTop: '2px' }}></i>
          <div>
            <p style={{ margin: 0, color: '#991b1b', fontSize: '14px', fontWeight: '600' }}>
              Error
            </p>
            <p style={{ margin: '4px 0 0 0', color: '#7f1d1d', fontSize: '13px' }}>
              {error}
            </p>
            <Button
              label="Retry"
              icon="pi pi-refresh"
              onClick={fetchSchools}
              loading={loadingSchools}
              style={{
                marginTop: '8px',
                background: '#dc2626',
                border: 'none',
                borderRadius: '8px',
                padding: '6px 16px',
                fontSize: '12px',
                color: 'white',
              }}
            />
          </div>
        </div>
      )}

      <div style={{
        background: '#f9fafb',
        borderRadius: '12px',
        padding: '20px',
        border: '1px solid #f1f5f9',
        marginBottom: '20px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <label style={{
            color: '#2855ae',
            fontSize: '13px',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            Select School to Activate
          </label>
          {renderRefreshButton()}
        </div>
        <Dropdown
          key={`school-dropdown-${allSchools.length}`}
          value={selectedSchoolId}
          onChange={(e) => setSelectedSchoolId(e.value)}
          options={allSchools}
          optionLabel="name"
          optionValue="id"
          placeholder={loadingSchools ? 'Loading...' : allSchools.length === 0 ? 'No schools available' : 'Select a school'}
          disabled={loadingSchools || allSchools.length === 0 || !!error}
          style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: '10px' }}
          panelStyle={{ borderRadius: '10px' }}
          filter
          filterBy="name"
          emptyMessage="No schools found"
        />
        {loadingSchools && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            marginTop: '8px',
            color: '#6b7280',
            fontSize: '12px'
          }}>
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '14px' }}></i>
            <span>Loading schools...</span>
          </div>
        )}
      </div>

      {allSchools.length > 0 && selectedSchoolId && (
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '16px',
            paddingBottom: '12px',
            borderBottom: '1px solid #f3f4f6',
          }}>
            <i className="pi pi-building" style={{ fontSize: '18px', color: '#2855ae' }}></i>
            <span style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
              {allSchools.find(s => s.id === selectedSchoolId)?.name}
            </span>
          </div>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '12px',
          }}>
            {allSchools.find(s => s.id === selectedSchoolId)?.school_id_code && (
              <div style={{ padding: '8px 0' }}>
                <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  School Code
                </div>
                <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937', marginTop: '2px' }}>
                  {allSchools.find(s => s.id === selectedSchoolId)?.school_id_code}
                </div>
              </div>
            )}
            
            {allSchools.find(s => s.id === selectedSchoolId)?.status && (
              <div style={{ padding: '8px 0' }}>
                <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Status
                </div>
                <div style={{ 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: getStatusColor(allSchools.find(s => s.id === selectedSchoolId)?.status),
                  marginTop: '2px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span style={{
                    display: 'inline-block',
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: getStatusColor(allSchools.find(s => s.id === selectedSchoolId)?.status),
                  }}></span>
                  {allSchools.find(s => s.id === selectedSchoolId)?.status?.toUpperCase()}
                </div>
              </div>
            )}

            {allSchools.find(s => s.id === selectedSchoolId)?.state && (
              <div style={{ padding: '8px 0' }}>
                <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  State
                </div>
                <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937', marginTop: '2px' }}>
                  {allSchools.find(s => s.id === selectedSchoolId)?.state}
                </div>
              </div>
            )}

            {allSchools.find(s => s.id === selectedSchoolId)?.district && (
              <div style={{ padding: '8px 0' }}>
                <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  District
                </div>
                <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937', marginTop: '2px' }}>
                  {allSchools.find(s => s.id === selectedSchoolId)?.district}
                </div>
              </div>
            )}

            {allSchools.find(s => s.id === selectedSchoolId)?.school_type && (
              <div style={{ padding: '8px 0' }}>
                <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  School Type
                </div>
                <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937', marginTop: '2px' }}>
                  {allSchools.find(s => s.id === selectedSchoolId)?.school_type}
                </div>
              </div>
            )}

            {allSchools.find(s => s.id === selectedSchoolId)?.pincode && (
              <div style={{ padding: '8px 0' }}>
                <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Pincode
                </div>
                <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937', marginTop: '2px' }}>
                  {allSchools.find(s => s.id === selectedSchoolId)?.pincode}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {allSchools.length === 0 && !loadingSchools && !error && (
        <div style={{
          background: '#fff7ed',
          border: '1px solid #fed7aa',
          borderRadius: '12px',
          padding: '40px 20px',
          textAlign: 'center',
          color: '#c2410c'
        }}>
          <i className="pi pi-inbox" style={{ fontSize: '32px', marginBottom: '12px', opacity: 0.6 }}></i>
          <p style={{ margin: 0, fontSize: '14px', fontWeight: '500' }}>
            No schools found in the system.
          </p>
          <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#9a3412' }}>
            Please contact your administrator to add schools.
          </p>
        </div>
      )}

      {/* Activate Button */}
      {selectedSchoolId && (
        <Button
          label={isActivating ? "Activating..." : "Activate School"}
          icon={isActivating ? "pi pi-spin pi-spinner" : "pi pi-check"}
          loading={isActivating}
          disabled={isActivating}
          onClick={handleActivateSchool}
          style={{
            width: '100%',
            background: isActivating ? '#94a3b8' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            border: 'none',
            borderRadius: '12px',
            padding: '14px',
            color: 'white',
            fontSize: '14px',
            fontWeight: '600',
            marginTop: '8px',
            boxShadow: isActivating ? 'none' : '0 4px 12px rgba(16, 185, 129, 0.3)',
          }}
        />
      )}
    </div>
  );

  return (
    <>
      <Dialog
        header={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              borderRadius: '12px',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <i className="pi pi-building" style={{ fontSize: '16px', color: 'white' }}></i>
            </div>
            <span style={{ color: '#ffffffff', fontSize: '18px', fontWeight: '600' }}>
              School Activation
            </span>
          </div>
        }
        visible={visible}
        style={{ width: '520px' }}
        onHide={onHide}
        draggable={false}
        resizable={false}
      >
        <div style={{ padding: '0 4px' }}>
          {/* School Count Badge */}
          {allSchools.length > 0 && !error && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              background: '#eff6ff',
              borderRadius: '20px',
              padding: '4px 14px',
              marginBottom: '16px',
            }}>
              <i className="pi pi-list" style={{ fontSize: '12px', color: '#3b82f6' }}></i>
              <span style={{ fontSize: '13px', color: '#1e40af', fontWeight: '500' }}>
                {allSchools.length} School{allSchools.length > 1 ? 's' : ''} Available
              </span>
            </div>
          )}
          
          {renderSchoolList()}
        </div>
      </Dialog>

      {/* Full-screen loading overlay */}
      {isSettingUpSchool && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            gap: '24px',
          }}
        >
          <ProgressSpinner
            style={{
              width: '80px',
              height: '80px',
            }}
            strokeWidth="4"
            animationDuration="1s"
          />
          <div
            style={{
              color: 'white',
              fontSize: '20px',
              fontWeight: '600',
              textAlign: 'center',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
            }}
          >
            Setting up School...
          </div>
        </div>
      )}
    </>
  );
};

export default SchoolSettingsModal;